<template>
  <b-container>
    <!-- Display loading spinner while data is being fetched -->
    <div v-if="loading" class="text-center my-5">
      <b-spinner label="Loading..."></b-spinner>
      <p class="mt-2">Loading form data...</p>
    </div>

    <!-- Render the form only when data is not loading and aspect data exists -->
    <form v-else-if="aspect && form.attributes.length" v-on:submit.prevent="saveForm">
      <h1>Edit Aspect {{ aspect.name }}</h1>
      <hr>
      <!-- Date Pickers using a v-if check on form.settings -->
      <div v-if="form.settings.date_new">
        <label>{{ form.settings.date_new }}</label>
        <DatePicker v-model="aspect.date_new" date-id="date_new" />
      </div>
      <div v-if="form.settings.date_purchased">
        <label>{{ form.settings.date_purchased }}</label>
        <DatePicker v-model="aspect.date_purchased" date-id="date_purchased" />
      </div>
      
      <!-- Loop through form attributes -->
      <div v-for="(attribute, i) in form.attributes" :key="attribute.id">
        <b-form-group :id="`input-group-${i}`" :label="attribute.label" :label-for="`input-${i}`"
          :description="attribute.tooltip">
          <!-- Conditional rendering for different attribute types -->
          <b-form-select :style="attribute.class" v-if="attribute.type === 'Select'"
            v-model="aspect['attribute' + attribute.column]" :options="attribute.value" :name="attribute.attribute_id" />
<!-- Phone input (masked) -->
<b-form-input
  v-if="attribute.type === 'Text' && attribute.label.toLowerCase().includes('phone')"
  :style="attribute.class"
  v-model="aspect['attribute' + attribute.column]"
  :name="attribute.attribute_id"
  v-mask="'(###) ###-####'"
/>

<!-- Regular text input (unmasked) -->
<b-form-input
  v-else-if="attribute.type === 'Text'"
  :style="attribute.class"
  v-model="aspect['attribute' + attribute.column]"
  :name="attribute.attribute_id"
/>
          <b-form-textarea :style="attribute.class" v-else-if="attribute.type === 'Textarea'"
            v-model="aspect['attribute' + attribute.column]" :name="attribute.attribute_id" />
          <b-form-checkbox :style="attribute.class" v-else-if="attribute.type === 'Checkbox'"
            v-model="aspect['attribute' + attribute.column]" :name="attribute.attribute_id" />
          <DatePicker v-model="aspect['attribute' + attribute.column]"
            :date-id="'attribute' + attribute.column" v-else-if="attribute.type === 'Date'" />
          <input v-else type="hidden" :name="attribute.attribute_id" :value="aspect['attribute' + attribute.column]" />
        </b-form-group>
      </div>

      <!-- Static form fields -->
      <label for="description">Details</label>
      <b-form-input id="description" v-model="aspect.description" class="mb-2"></b-form-input>
      <b-form-group v-if="form.settings.color === 1" label="Color">
        <b-form-select id="opt_color" v-model="aspect.opt_color" :options="colorOptions"></b-form-select>
      </b-form-group>
      <b-form-group v-if="form.settings.star === 1" label="Star">
        <b-form-select id="opt_star" v-model="aspect.opt_star" :options="starOptions"></b-form-select>
      </b-form-group>
      <b-form-group v-if="form.settings.status === 1" label="Status">
        <b-form-select id="opt_status" v-model="aspect.opt_status" :options="statusOptions"></b-form-select>
      </b-form-group>
      <b-form-group v-if="form.settings.flag === 1" label="Flag">
        <b-form-select id="opt_flag" v-model="aspect.opt_flag" :options="flagOptions"></b-form-select>
      </b-form-group>
      
      <b-button type="submit" :click="saveForm" variant="primary">Save and Update</b-button>
    </form>
    
    <!-- Show error message if data could not be fetched -->
    <div v-else class="text-center my-5">
      <p>Aspect not found or an error occurred.</p>
    </div>
  </b-container>
</template>

<script>
import FormDataService from "@/services/FormDataService.js";
import AspectDataService from "@/services/AspectDataService.js";
import DatePicker from '@/components/DatePicker.vue';

export default {
  components: {
    DatePicker
  },
  props: ["company"],
  data() {
    return {
      aspect: null, // Initialize as null to manage loading state
      form: {
        settings: {},
        attributes: [],
      },
      loading: true, // Add a loading state
      sidebarad: false,
      component: "b-form-input",
      // Removed date_new and date_purchased data properties as they are redundant
      colorOptions: [
        { text: 'None', value: '1' },
        { text: 'Red', value: '2' },
        { text: 'Blue', value: '3' },
        { text: 'Green', value: '4' },
        { text: 'Yellow', value: '5' },
      ],
      starOptions: [
        { text: 'No', value: '0' },
        { text: 'Yes', value: '1' },
      ],
      statusOptions: [
        { text: 'New', value: '1' },
        { text: 'Prospect', value: '2' },
        { text: 'Verified', value: '3' },
        { text: 'Active', value: '4' },
        { text: 'Deleted', value: '5' },
      ],
      flagOptions: [
        { text: 'No', value: '0' },
        { text: 'Yes', value: '1' },
      ],
    };
  },
  // Use a single async function to fetch all data sequentially
  async created() {
  this.loading = true;

  try {
    // 1. Fetch aspect data
    const aspectRes = await AspectDataService.getById(this.$route.params.aspect_key);
    if (!aspectRes || !aspectRes.data) {
      throw new Error('Failed to load aspect data');
    }
    const rawAspectData = aspectRes.data;

    // 2. Fetch form structure
    const formRes = await FormDataService.get(rawAspectData.aspect_form_id);
    const formData = formRes?.data || formRes;
    if (!formData || (!formData.json && !formData.attributes)) {
      throw new Error('Failed to load form structure');
    }
    this.form = formData.json || formData;

    // 3. Create a clean reactive aspect object
    const aspect = {};

    // 4. Copy over all keys from raw data
    Object.keys(rawAspectData).forEach((key) => {
      this.$set(aspect, key, rawAspectData[key]);
    });

    // 5. Make sure all attribute keys are present
    if (this.form && Array.isArray(this.form.attributes)) {
      this.form.attributes.forEach((attr) => {
        const attrKey = 'attribute' + attr.column;
        if (!Object.prototype.hasOwnProperty.call(aspect, attrKey)) {
          this.$set(aspect, attrKey, null);
        }
      });
    }

    // 6. Format date fields
    Object.keys(aspect).forEach((key) => {
      if (key.startsWith('date_') && aspect[key]) {
        const d = new Date(aspect[key]);
        if (!isNaN(d)) {
          const mm = String(d.getMonth() + 1).padStart(2, '0');
          const dd = String(d.getDate()).padStart(2, '0');
          const yyyy = d.getFullYear();
          aspect[key] = `${mm}/${dd}/${yyyy}`;
        }
      }
    });

    // 7. Assign final aspect object to component state
    this.aspect = aspect;

  } catch (error) {
    console.error('Error fetching aspect or form:', error);
    this.aspect = null;
  } finally {
    this.loading = false;
  }
}
,
  methods: {
    // This function is no longer needed since the data is fetched in created()
    // It has been removed.
    // formatDate: function(data_date) { ... }

    saveForm: function () {
      this.setName();
      AspectDataService.update(this.$route.params.aspect_key, this.aspect).then((res) => {
        if (res && !res.status) {
          window.location = `/aspect/${this.$route.params.aspect_key}/?update=success`;
        } else {
          console.error('Failed to save aspect:', res?.message);
        }
      }).catch((err) => {
        console.error('Failed to save aspect:', err);
      });
    },

    setName: function () {
      let form_detail = this.form;
      this.aspect.name = '';

      if (form_detail.settings && form_detail.settings.searchfields) {
        form_detail.settings.searchfields.forEach(element => {
          let attr = form_detail.attributes.find(x => x.column === element);
          if (attr && this.aspect[`attribute${attr.column}`] !== null) {
            this.aspect.name += this.aspect[`attribute${attr.column}`] + " ";
          }
        });
      }
    }
  },
};
</script>
